import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, Mail, Palette, LifeBuoy, ChevronRight } from 'lucide-react'
import { useAuth } from '../lib/auth'
import ThemePicker from './ThemePicker'

/**
 * @param {{ open: boolean, onClose: () => void }} props
 */
export default function ProfileModal({ open, onClose }) {
  const { profile, updateProfile } = useAuth()
  const navigate = useNavigate()
  const [sendFromEmail, setSendFromEmail] = useState(profile?.send_from_email || '')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const profileModalRef = useRef(/** @type {HTMLDivElement | null} */ (null))

  /** @param {string} path */
  function goTo(path) {
    onClose()
    navigate(path)
  }

  useEffect(() => {
    if (!open) return
    profileModalRef.current?.focus()
    /** @param {KeyboardEvent} e */
    function handleKeyDown(e) { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open])

  if (!open) return null

  async function handleSave() {
    setSaving(true)
    try {
      await updateProfile({ send_from_email: sendFromEmail || null })
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e)
      alert('Error saving: ' + message)
    }
    setSaving(false)
  }

  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
      <div ref={profileModalRef} tabIndex={-1} className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Profile Settings</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Name</label>
            <p className="text-sm text-gray-900">{profile?.display_name || 'User'}</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Role</label>
            <p className="text-sm text-gray-900 capitalize">{profile?.role || 'Unknown'}</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Login Email</label>
            <p className="text-sm text-gray-500">{profile?.email || '—'}</p>
          </div>
          <div className="pt-2 border-t border-gray-100">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <span className="flex items-center gap-1.5"><Mail size={14} /> Send-From Email</span>
            </label>
            <p className="text-xs text-gray-400 mb-2">When sending invoices or dispatch notifications, your email client will open with this address. Make sure this account is set up in your email app (Outlook, Gmail, etc.).</p>
            <input
              type="email"
              value={sendFromEmail}
              onChange={e => setSendFromEmail(e.target.value)}
              placeholder="e.g. info@yourcompany.com"
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-brand-300 focus:border-brand-500 outline-none"
            />
          </div>
          <div className="pt-4 border-t border-gray-100">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <span className="flex items-center gap-1.5"><Palette size={14} /> Appearance</span>
            </label>
            <p className="text-xs text-gray-400 mb-3">Theme is saved to this browser. Changes apply instantly.</p>
            <ThemePicker />
          </div>
          <div className="pt-4 border-t border-gray-100">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <span className="flex items-center gap-1.5"><LifeBuoy size={14} /> Help & Legal</span>
            </label>
            <div className="rounded-lg border border-gray-200 divide-y divide-gray-200 overflow-hidden">
              <button type="button" onClick={() => goTo('/help')} className="w-full flex items-center justify-between px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                <span>Help & Support</span>
                <ChevronRight size={14} className="text-gray-400" />
              </button>
              <button type="button" onClick={() => goTo('/terms')} className="w-full flex items-center justify-between px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                <span>Terms of Service</span>
                <ChevronRight size={14} className="text-gray-400" />
              </button>
              <button type="button" onClick={() => goTo('/privacy')} className="w-full flex items-center justify-between px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                <span>Privacy Policy</span>
                <ChevronRight size={14} className="text-gray-400" />
              </button>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          {saved && <span className="text-sm text-[var(--color-success)] font-medium">Saved!</span>}
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Close</button>
          <button onClick={handleSave} disabled={saving} className="px-5 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50">
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}
